class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      // Check if we're on the cart page, and if so, don't open the drawer
      if (!window.location.pathname.includes('/cart')) {
        this.open(cartLink);
      } else {
        window.location.reload();
        return;
      }
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        // Also check for the cart page in the keyboard handler
        if (!window.location.pathname.includes('/cart')) {
          this.open(cartLink);
        } else {
          window.location.reload();
          return;
        }
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);

        // Publish an event when the cart drawer is fully opened
        publish(PUB_SUB_EVENTS.cartDrawerOpen, { source: 'cart-drawer' });
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (window.templateData.name === 'cart') {
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth';

      window.scrollTo({ behavior, left: 0, top: 0 });
      return;
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    // Save app blocks before re-render
    const cartItemsContainer = this.querySelector('#CartDrawer-CartItems');
    const savedAppBlocks = [];
    if (cartItemsContainer) {
      const cartWrapper = cartItemsContainer.querySelector('.drawer__cart-items-wrapper');
      cartItemsContainer.querySelectorAll('[id^="shopify-block"]').forEach((block) => {
        savedAppBlocks.push({
          element: block,
          isBeforeCartItems: cartWrapper ? block.compareDocumentPosition(cartWrapper) & Node.DOCUMENT_POSITION_FOLLOWING : true
        });
      });
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Restore app blocks after re-render
    if (savedAppBlocks.length) {
      const newCartItemsContainer = this.querySelector('#CartDrawer-CartItems');
      if (newCartItemsContainer) {
        const cartWrapper = newCartItemsContainer.querySelector('.drawer__cart-items-wrapper');
        const liveRegion = newCartItemsContainer.querySelector('#CartDrawer-LiveRegionText');
        for (const saved of savedAppBlocks) {
          if (saved.isBeforeCartItems && cartWrapper) {
            newCartItemsContainer.insertBefore(saved.element, cartWrapper);
          } else if (liveRegion) {
            newCartItemsContainer.insertBefore(saved.element, liveRegion);
          } else {
            newCartItemsContainer.appendChild(saved.element);
          }
        }
      }
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    // Base sections always needed
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      }
    ];

    // Only add cart page sections if we're on the cart page
    if (window.location.pathname.includes('/cart')) {
      // Check for main cart elements and add them to sections if they exist
      const cartElements = {
        'main-cart-items': '.js-contents',
        'main-cart-footer': '.js-contents',
        'cart-page-item-count': '.cart-page-item-count'
      };

      for (const [id, selector] of Object.entries(cartElements)) {
        const element = document.getElementById(id);
        if (element) {
          sections.push({
            id: id,
            section: element.dataset.id,
            selector: selector
          });
        }
      }
    }

    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
